import { buildTileUrl, type ImagerySource } from '../../../map/services/imagerySources'
import {
  ATTRIBUTION_TEXT_PT,
  BADGE_HEIGHT_PT,
  BADGE_PAD_PT,
  BADGE_TEXT_PT,
  TILE_SIZE,
  truncateLabel,
} from './siteMapGeometry'
import type { TileSession } from './tileBudget'

/**
 * Canvas drawing primitives shared by the single site map and the map book
 * (009). Every function draws in the caller's logical units; nothing here
 * knows about print scale except through the sizes it is handed.
 */

const MIN_ZOOM = 3
/** Missing tiles borrow from up to this many parent zoom levels. */
const MAX_PARENT_FALLBACK_LEVELS = 2
export const MARKER_COLOR = '#0E7490'
export const MISSING_IMAGERY_FILL = '#d4d4d4'

/** Destination rectangle of one tile on the canvas, in logical px */
interface TileRect {
  x: number
  y: number
  w: number
  h: number
}

/**
 * Draw one tile cell from the first source that can supply it: each source's
 * own tile, then its scaled parent-tile quadrants, before moving on to the
 * next source. Mirrors the interactive map, where the Esri underlay shows
 * through wherever the regional overlay 404s — without this, a tile at the
 * edge of regional coverage drew as a grey hole. Returns the source used.
 */
export async function drawTileWithFallback(
  ctx: CanvasRenderingContext2D,
  session: TileSession,
  sources: ImagerySource[],
  zoom: number,
  tx: number,
  ty: number,
  rect: TileRect
): Promise<ImagerySource | null> {
  for (const source of sources) {
    if (zoom < source.minZoom) continue
    // Shallower sources serve this cell from a parent tile beyond their depth
    const depthGap = Math.max(0, zoom - source.maxNativeZoom)
    for (let level = depthGap; level <= depthGap + MAX_PARENT_FALLBACK_LEVELS; level++) {
      const pz = zoom - level
      if (pz < Math.max(MIN_ZOOM, source.minZoom)) break
      const px = tx >> level
      const py = ty >> level
      const img = await session.load(buildTileUrl(source, pz, px, py), {
        sourceId: source.id,
        z: pz,
        x: px,
        y: py,
      })
      if (!img) continue // try the next level up, then the next source
      const sub = TILE_SIZE / 2 ** level
      const sx = (tx - (px << level)) * sub
      const sy = (ty - (py << level)) * sub
      ctx.drawImage(img, sx, sy, sub, sub, rect.x, rect.y, rect.w, rect.h)
      return source
    }
  }
  return null
}

/**
 * Fetch and draw every tile intersecting a `width` × `height` canvas. Tiles
 * come from `tileZoom` (default: the whole zoom below the fractional `zoom`)
 * and are scaled up to fit; edges are rounded so neighbouring tiles share a
 * pixel boundary (no hairline seams). Returns the sources that supplied at
 * least one tile, in fallback order.
 */
export async function drawTiles(
  ctx: CanvasRenderingContext2D,
  session: TileSession,
  sources: ImagerySource[],
  frame: {
    zoom: number
    originX: number
    originY: number
    width: number
    height: number
    tileZoom?: number
  }
): Promise<ImagerySource[]> {
  const { zoom, originX, originY, width, height } = frame
  ctx.fillStyle = MISSING_IMAGERY_FILL
  ctx.fillRect(0, 0, width, height)

  const tileZoom = frame.tileZoom ?? Math.floor(zoom)
  const drawSize = TILE_SIZE * 2 ** (zoom - tileZoom)
  const tileMin = { x: Math.floor(originX / drawSize), y: Math.floor(originY / drawSize) }
  const tileMax = {
    x: Math.floor((originX + width) / drawSize),
    y: Math.floor((originY + height) / drawSize),
  }
  const maxTileIndex = Math.pow(2, tileZoom) - 1
  const edge = (i: number, origin: number) => Math.round(i * drawSize - origin)

  const jobs: Array<Promise<ImagerySource | null>> = []
  for (let tx = tileMin.x; tx <= tileMax.x; tx++) {
    for (let ty = tileMin.y; ty <= tileMax.y; ty++) {
      if (tx < 0 || ty < 0 || tx > maxTileIndex || ty > maxTileIndex) continue
      const x = edge(tx, originX)
      const y = edge(ty, originY)
      const rect = { x, y, w: edge(tx + 1, originX) - x, h: edge(ty + 1, originY) - y }
      jobs.push(drawTileWithFallback(ctx, session, sources, tileZoom, tx, ty, rect))
    }
  }
  const used = new Set(await Promise.all(jobs))
  return sources.filter((s) => used.has(s))
}

/** Site boundary polygon: dashed amber outline with a faint fill. `unit`
    scales the stroke for canvases drawn in other units (map-book sheets). */
export function drawBoundary(
  ctx: CanvasRenderingContext2D,
  points: Array<{ x: number; y: number }>,
  unit = 1
): void {
  if (points.length < 3) return
  ctx.save()
  ctx.beginPath()
  points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)))
  ctx.closePath()
  ctx.fillStyle = 'rgba(245, 158, 11, 0.08)'
  ctx.fill()
  ctx.setLineDash([12 * unit, 7 * unit])
  ctx.strokeStyle = '#f59e0b'
  ctx.lineWidth = 3.5 * unit
  ctx.lineJoin = 'round'
  ctx.shadowColor = 'rgba(0, 0, 0, 0.4)'
  ctx.shadowBlur = 2 * unit
  ctx.stroke()
  ctx.restore()
}

/** Badge metrics in the caller's logical units */
export interface BadgeMetrics {
  /** Text size */
  fontPx: number
  /** Badge height (and minimum width) */
  heightPx: number
  /** Padding either side of the text */
  padPx: number
}

/**
 * Numbered badge centred on the tree: a circle for short labels, a pill for
 * longer tags. Replaces the old teardrop pin + label chip, which needed ~6×
 * the area per tree and buried dense sites under overlapping chips.
 */
export function drawMarker(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  label: string,
  metrics: BadgeMetrics
): void {
  const text = truncateLabel(label)
  const height = metrics.heightPx
  ctx.save()
  ctx.font = `700 ${metrics.fontPx}px system-ui, -apple-system, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const width = Math.max(height, ctx.measureText(text).width + 2 * metrics.padPx)

  ctx.beginPath()
  ctx.roundRect(x - width / 2, y - height / 2, width, height, height / 2)
  ctx.fillStyle = MARKER_COLOR
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = (2 * height) / 22
  ctx.shadowColor = 'rgba(0, 0, 0, 0.45)'
  ctx.shadowBlur = 3
  ctx.shadowOffsetY = 1
  ctx.fill()
  ctx.shadowColor = 'transparent'
  ctx.stroke()

  if (text) {
    ctx.fillStyle = '#ffffff'
    ctx.fillText(text, x, y + height / 22)
  }
  ctx.restore()
}

/** Imagery credit strip — tile licences require attribution on the output */
export function drawAttribution(
  ctx: CanvasRenderingContext2D,
  text: string,
  width: number,
  height: number,
  scale = 1,
  /** No tile reached this frame (009 map book): say so rather than look broken */
  missingImagery = false
): void {
  if (missingImagery) text = text ? `Imagery unavailable · ${text}` : 'Imagery unavailable'
  if (!text) return
  ctx.save()
  ctx.font = `${13 * scale}px system-ui, -apple-system, sans-serif`
  const stripH = 22 * scale
  const attrW = ctx.measureText(text).width + 14 * scale
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)'
  ctx.fillRect(width - attrW, height - stripH, attrW, stripH)
  ctx.fillStyle = '#ffffff'
  ctx.textAlign = 'right'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, width - 7 * scale, height - stripH / 2)
  ctx.restore()
}

// ---------------------------------------------------------------------------
// Map book primitives (009). Sizes are in printed points; `unitsPerPt`
// converts them to the canvas's drawing units.
// ---------------------------------------------------------------------------

const INK = '#1f2937'
const MUTED_DOT = 'rgba(107, 114, 128, 0.5)'

/** Unlabelled tree dot with a white halo — the overview plots every tree (FR-016) */
export function drawTreeDot(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  unitsPerPt: number,
  diameterPt = 3
): void {
  ctx.save()
  ctx.beginPath()
  ctx.arc(x, y, (diameterPt / 2) * unitsPerPt, 0, Math.PI * 2)
  ctx.fillStyle = MARKER_COLOR
  ctx.fill()
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 0.75 * unitsPerPt
  ctx.stroke()
  ctx.restore()
}

/** A tree in this frame that belongs to another sheet: grey, unnumbered (FR-010) */
export function drawMutedDot(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  unitsPerPt: number
): void {
  ctx.save()
  ctx.beginPath()
  ctx.arc(x, y, 1.5 * unitsPerPt, 0, Math.PI * 2)
  ctx.fillStyle = MUTED_DOT
  ctx.fill()
  ctx.restore()
}

/** Leader from a displaced badge to its tree: a dark line on a white casing,
    ending in a 2 pt dot at the true position. Long leaders print heavier. */
export function drawLeaderLine(
  ctx: CanvasRenderingContext2D,
  from: { x: number; y: number },
  to: { x: number; y: number },
  unitsPerPt: number,
  long = false
): void {
  const width = (long ? 1 : 0.75) * unitsPerPt
  ctx.save()
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.moveTo(from.x, from.y)
  ctx.lineTo(to.x, to.y)
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = width + 1 * unitsPerPt
  ctx.stroke()
  ctx.strokeStyle = INK
  ctx.lineWidth = width
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(from.x, from.y, 1 * unitsPerPt, 0, Math.PI * 2)
  ctx.fillStyle = INK
  ctx.fill()
  ctx.restore()
}

/** Numbered badge filling a planned rectangle (canvas units) */
export function drawBadgeAt(
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; w: number; h: number },
  label: string,
  unitsPerPt: number
): void {
  const text = truncateLabel(label)
  ctx.save()
  ctx.beginPath()
  ctx.roundRect(rect.x, rect.y, rect.w, rect.h, rect.h / 2)
  ctx.fillStyle = MARKER_COLOR
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 0.75 * unitsPerPt
  ctx.fill()
  ctx.stroke()
  if (text) {
    ctx.font = `700 ${BADGE_TEXT_PT * unitsPerPt}px system-ui, -apple-system, sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = '#ffffff'
    ctx.fillText(text, rect.x + rect.w / 2, rect.y + rect.h / 2 + 0.4 * unitsPerPt)
  }
  ctx.restore()
}

/** A sheet's footprint on the overview: 2 pt outline on a white halo, with
    an "N · k trees" pill at its top-left corner */
export function drawSheetOutline(
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; w: number; h: number },
  label: string,
  unitsPerPt: number
): void {
  ctx.save()
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 3.5 * unitsPerPt
  ctx.strokeRect(rect.x, rect.y, rect.w, rect.h)
  ctx.strokeStyle = INK
  ctx.lineWidth = 2 * unitsPerPt
  ctx.strokeRect(rect.x, rect.y, rect.w, rect.h)

  ctx.font = `700 ${BADGE_TEXT_PT * unitsPerPt}px system-ui, -apple-system, sans-serif`
  const h = BADGE_HEIGHT_PT * unitsPerPt
  const w = ctx.measureText(label).width + 2 * BADGE_PAD_PT * unitsPerPt
  const x = rect.x + 2 * unitsPerPt
  const y = rect.y + 2 * unitsPerPt
  ctx.beginPath()
  ctx.roundRect(x, y, w, h, h / 2)
  ctx.fillStyle = INK
  ctx.fill()
  ctx.fillStyle = '#ffffff'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText(label, x + BADGE_PAD_PT * unitsPerPt, y + h / 2 + 0.4 * unitsPerPt)
  ctx.restore()
}

export { scaleBarLength } from './siteMapGeometry'

/** Metric scale bar in its planned box: alternating black/white segments + "N m" */
export function drawScaleBar(
  ctx: CanvasRenderingContext2D,
  box: { x: number; y: number; w: number; h: number },
  metres: number,
  barPt: number
): void {
  const segments = 4
  const h = 3.5
  const x = box.x + 4
  const y = box.y + box.h - 5 - h
  ctx.save()
  // White backing so the bar reads over any imagery
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
  ctx.fillRect(box.x, box.y, box.w, box.h)
  for (let i = 0; i < segments; i++) {
    ctx.fillStyle = i % 2 === 0 ? INK : '#ffffff'
    ctx.fillRect(x + (i * barPt) / segments, y, barPt / segments, h)
  }
  ctx.strokeStyle = INK
  ctx.lineWidth = 0.5
  ctx.strokeRect(x, y, barPt, h)
  ctx.fillStyle = INK
  ctx.font = `700 ${BADGE_TEXT_PT}px system-ui, -apple-system, sans-serif`
  ctx.textAlign = 'left'
  ctx.textBaseline = 'bottom'
  ctx.fillText(`${metres} m`, x + barPt + 4, y + h + 1)
  ctx.restore()
}

/**
 * Locator inset in its planned box: the site extent, every sheet's outline
 * and this sheet filled in the marker colour, so a reader holding one sheet
 * knows where it sits. Pure vector — no imagery needed.
 */
export function drawLocatorInset(
  ctx: CanvasRenderingContext2D,
  box: { x: number; y: number; w: number; h: number },
  /** Sheet outlines in any common planar space (e.g. overview px) */
  outlines: Array<{ number: number; rect: { x: number; y: number; w: number; h: number } }>,
  current: number
): void {
  if (outlines.length === 0) return
  const minX = Math.min(...outlines.map((o) => o.rect.x))
  const minY = Math.min(...outlines.map((o) => o.rect.y))
  const maxX = Math.max(...outlines.map((o) => o.rect.x + o.rect.w))
  const maxY = Math.max(...outlines.map((o) => o.rect.y + o.rect.h))
  const pad = 4
  const k = Math.min(
    (box.w - 2 * pad) / Math.max(maxX - minX, 1e-9),
    (box.h - 2 * pad) / Math.max(maxY - minY, 1e-9)
  )
  // Letterboxed: the site keeps its shape, centred in the box
  const offX = box.x + (box.w - (maxX - minX) * k) / 2
  const offY = box.y + (box.h - (maxY - minY) * k) / 2
  const map = (r: { x: number; y: number; w: number; h: number }) => ({
    x: offX + (r.x - minX) * k,
    y: offY + (r.y - minY) * k,
    w: r.w * k,
    h: r.h * k,
  })

  ctx.save()
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
  ctx.fillRect(box.x, box.y, box.w, box.h)
  ctx.strokeStyle = INK
  ctx.lineWidth = 0.5
  ctx.strokeRect(box.x, box.y, box.w, box.h)
  const here = outlines.find((o) => o.number === current)
  if (here) {
    const r = map(here.rect)
    ctx.fillStyle = MARKER_COLOR
    ctx.fillRect(r.x, r.y, r.w, r.h)
  }
  for (const o of outlines) {
    const r = map(o.rect)
    ctx.strokeRect(r.x, r.y, r.w, r.h)
  }
  ctx.restore()
}

/** Imagery credit in its planned box, flush to the box's left or right edge */
export function drawAttributionAt(
  ctx: CanvasRenderingContext2D,
  text: string,
  box: { x: number; y: number; w: number; h: number; align: 'left' | 'right' },
  missingImagery = false
): void {
  if (missingImagery) text = text ? `Imagery unavailable · ${text}` : 'Imagery unavailable'
  if (!text) return
  ctx.save()
  ctx.font = `${ATTRIBUTION_TEXT_PT}px system-ui, -apple-system, sans-serif`
  const w = Math.min(box.w, ctx.measureText(text).width + 7)
  const x = box.align === 'right' ? box.x + box.w - w : box.x
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)'
  ctx.fillRect(x, box.y, w, box.h)
  ctx.fillStyle = '#ffffff'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, x + 3.5, box.y + box.h / 2)
  ctx.restore()
}
